import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ClockIcon,
  PencilSquareIcon,
  PlayIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import { TRANSCRIPT_PROVIDERS } from '../../../../../constants/transcriptProviders';
import { TRANSCRIPTION_LANGUAGES } from '../../../../../constants/transcriptionLanguages';
import TranscriptStatusDisplay from './TranscriptStatusDisplay';
import { isRunningStatus } from '../../../../../constants/jobStatus';

const toExportBlocks = (segments) => segments.map((segment, index) => ({
  id: segment.id != null ? String(segment.id) : `segment-${index}`,
  text: segment.text || segment.content || '',
  speaker: segment.speaker || segment.speaker_name || segment.speakerName || 'UNKNOWN',
  start: segment.start || segment.start_time || 0,
  end: segment.end || segment.end_time || 0,
  words: segment.words || [],
  paragraphNumber: segment.paragraph_number || index,
}));

/** Transcript state and actions: start a first run, follow progress, or edit / export / fix / replace. */
export default function TranscriptCard({ session, segments, ready, generation, onFixSections, notify }) {
  const [isExporting, setIsExporting] = useState(false);
  const status = session.transcript_status;
  const busy = isRunningStatus(status);

  const exportPdf = async () => {
    setIsExporting(true);
    try {
      const { downloadTranscriptPdf } = await import('../../../../transcript-editor/utils/transcriptPdf');
      downloadTranscriptPdf(toExportBlocks(segments), session.title || 'Transcript');
    } catch (err) {
      notify({ type: 'error', title: 'Download failed', message: err.message || 'Could not export transcript to PDF.' });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="session-detail-card">
      <h3>Transcript</h3>

      {status && status !== 'COMPLETED' && (
        <TranscriptStatusDisplay
          status={status}
          startedAt={session.transcript_processing_started_at}
          completedAt={session.transcript_processing_completed_at}
          duration={session.transcript_processing_duration}
          errorMessage={session.transcript_error_message}
          provider={session.transcript_provider}
          compact
        />
      )}

      {!ready && !busy && (
        <div className="session-side-actions">
          <label className="session-side-label" htmlFor="session-provider-select">Provider</label>
          <select
            id="session-provider-select"
            value={generation.provider}
            onChange={(e) => generation.setProvider(e.target.value)}
            className="session-side-select"
          >
            {TRANSCRIPT_PROVIDERS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
          <label className="session-side-label" htmlFor="session-language-select">Language</label>
          <select
            id="session-language-select"
            value={generation.language}
            onChange={(e) => generation.setLanguage(e.target.value)}
            className="session-side-select"
          >
            {TRANSCRIPTION_LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
          </select>
          <button
            type="button"
            className="session-btn session-btn-secondary"
            onClick={status === 'FAILED' ? generation.openReplace : generation.start}
            disabled={generation.isStarting}
          >
            {generation.isStarting ? <ClockIcon className="animate-spin" /> : <PlayIcon />}
            {status === 'FAILED' ? 'Retry transcript' : 'Generate transcript'}
          </button>
        </div>
      )}

      {ready && !busy && (
        <div className="session-side-actions">
          <Link to={`/sessions/${session.id}/edit`} className="session-btn session-btn-secondary session-btn-full">
            <PencilSquareIcon /> Edit transcript
          </Link>
          {segments.length > 0 && (
            <button type="button" className="session-btn session-btn-ghost" onClick={exportPdf} disabled={isExporting}>
              {isExporting ? <ArrowPathIcon className="animate-spin" /> : <ArrowDownTrayIcon />}
              {isExporting ? 'Preparing PDF...' : 'Download transcript PDF'}
            </button>
          )}
          <button type="button" className="session-btn session-btn-ghost" onClick={onFixSections}>
            <WrenchScrewdriverIcon /> Fix missing sections
          </button>
          <button type="button" className="session-side-link" onClick={generation.openReplace}>
            Regenerate transcript
          </button>
        </div>
      )}
    </div>
  );
}
